import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { useConfig } from './ConfigContext';
import { setTokenGetter } from '../utils';

export interface SupabaseUser {
  id: string;
  email?: string;
  user_metadata?: Record<string, any>;
}

export interface SupabaseContextType {
  user: SupabaseUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  accessToken: string | null;
  supabase: SupabaseClient | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  handlePasswordReset: (email: string) => Promise<void>;
}

const SupabaseContext = createContext<SupabaseContextType | undefined>(undefined);

interface SupabaseProviderProps {
  children: ReactNode;
}

export function SupabaseProvider({ children }: SupabaseProviderProps) {
  const { config, loading: configLoading } = useConfig();
  const [supabase, setSupabase] = useState<SupabaseClient | null>(null);
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const clientInitializedRef = useRef(false);
  const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);

  // Initialize Supabase client when config is available (only once)
  useEffect(() => {
    // Prevent multiple initializations - check both ref and existing client
    if (clientInitializedRef.current || supabase !== null) {
      return;
    }

    if (configLoading) {
      return;
    }

    if (!config) {
      console.error('Configuration is missing: config is null');
      setIsLoading(false);
      return;
    }

    if (!config.supabase) {
      console.error('Supabase configuration is missing: supabase property not found in config', {
        configKeys: Object.keys(config),
        config
      });
      setIsLoading(false);
      return;
    }

    const { url, publishableKey } = config.supabase;

    if (!url || !publishableKey) {
      console.error('Supabase configuration is incomplete');
      setIsLoading(false);
      return;
    }

    // Mark as initialized to prevent multiple instances
    clientInitializedRef.current = true;

    // Custom storage implementation using sessionStorage
    const sessionStorageAdapter = {
      getItem: (key: string) => {
        return sessionStorage.getItem(key);
      },
      setItem: (key: string, value: string) => {
        sessionStorage.setItem(key, value);
      },
      removeItem: (key: string) => {
        sessionStorage.removeItem(key);
      }
    };

    const client = createClient(url, publishableKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        storage: sessionStorageAdapter
      }
    });

    setSupabase(client);

    // Get initial session
    client.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error('Error getting initial session:', error);
        setIsLoading(false);
        return;
      }

      if (session) {
        setUser(session.user as SupabaseUser);
        setAccessToken(session.access_token);
        setIsAuthenticated(true);
      }
      setIsLoading(false);
    });

    // Listen to auth state changes
    const {
      data: { subscription },
    } = client.auth.onAuthStateChange((_event, session) => {
      if (session) {
        setUser(session.user as SupabaseUser);
        setAccessToken(session.access_token);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setAccessToken(null);
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    });

    // Store subscription for cleanup
    subscriptionRef.current = subscription;

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
      // Don't reset the ref here - we want to prevent multiple instances even in React Strict Mode
      // The ref will persist across remounts, preventing duplicate clients
    };
  }, [config?.supabase?.url, config?.supabase?.publishableKey, configLoading]);

  // Set token getter for axios interceptor
  useEffect(() => {
    if (supabase) {
      setTokenGetter(async () => {
        try {
          const { data: { session }, error } = await supabase.auth.getSession();
          if (error) {
            console.error('Error getting session in token getter:', error);
            return null;
          }
          return session?.access_token || null;
        } catch (error) {
          console.error('Error in token getter:', error);
          return null;
        }
      });
      console.log('Token getter function has been set');
    }
  }, [supabase]);

  const login = useCallback(async (email: string, password: string) => {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      throw error;
    }

    if (data.session) {
      setUser(data.user as SupabaseUser);
      setAccessToken(data.session.access_token);
      setIsAuthenticated(true);
    }
  }, [supabase]);

  const logout = useCallback(async () => {
    if (!supabase) {
      return;
    }

    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error signing out:', error);
      // Continue with logout even if there's an error
    }

    setUser(null);
    setAccessToken(null);
    setIsAuthenticated(false);
  }, [supabase]);

  const signUp = useCallback(async (email: string, password: string) => {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });

    if (error) {
      throw error;
    }

    // Note: User will need to confirm email if email confirmation is enabled
    // For now, we'll set the user if session is available
    if (data.session) {
      setUser(data.user as SupabaseUser);
      setAccessToken(data.session.access_token);
      setIsAuthenticated(true);
    }
  }, [supabase]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!supabase) {
      return null;
    }

    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) {
        console.error('Error getting session:', error);
        return null;
      }
      return session?.access_token || null;
    } catch (error) {
      console.error('Error getting access token:', error);
      return null;
    }
  }, [supabase]);

  const handlePasswordReset = useCallback(async (email: string) => {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      throw error;
    }
  }, [supabase]);

  return (
    <SupabaseContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated,
        accessToken,
        supabase,
        login,
        logout,
        signUp,
        getAccessToken,
        handlePasswordReset,
      }}
    >
      {children}
    </SupabaseContext.Provider>
  );
}

export function useSupabase() {
  const context = useContext(SupabaseContext);
  if (context === undefined) {
    throw new Error('useSupabase must be used within a SupabaseProvider');
  }
  return context;
}
